import { API_CONFIG } from '../config/api.js';

const isAbsoluteUrl = (url) => /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//');
const isImageApiUrl = (url) => url.startsWith('/api/images/') || url.startsWith('api/images/');

/**
 * Convert relative image API URLs to the configured backend origin.
 *
 * Staging/prod Firebase Hosting serves the frontend from a different origin
 * than the API. A relative `/api/images/...` URL therefore hits the frontend
 * shell unless we pin it to `VITE_API_BASE_URL`.
 *
 * @param {string|null} url
 * @param {string} [backendBaseUrl]
 * @returns {string|null}
 */
export function resolveBackendMediaUrl(url, backendBaseUrl = API_CONFIG.BACKEND_URL) {
  if (!url) return url;

  const rawUrl = String(url);
  if (
    rawUrl.startsWith('blob:') ||
    rawUrl.startsWith('data:') ||
    isAbsoluteUrl(rawUrl) ||
    !isImageApiUrl(rawUrl) ||
    !backendBaseUrl
  ) {
    return url;
  }

  try {
    const path = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    return new URL(path, backendBaseUrl).toString();
  } catch {
    const base = String(backendBaseUrl).replace(/\/$/, '');
    const path = rawUrl.replace(/^\/+/, '');
    return `${base}/${path}`;
  }
}

/**
 * Pick the best thumbnail URL for a desired size, preferring the Cloud CDN
 * variant when the API has emitted one (BOU-768 lazy-fill — see
 * backend/src/gaia/api/routes/images.py emission of cdn_thumbnail_urls).
 *
 * @param {string|null} url - Resolved image URL (proxy or CDN form)
 * @param {number} size - Desired thumbnail size (64, 128, 256, 512)
 * @param {Object|null} [cdnThumbnailUrls] - Optional `{size: cdnUrl}` map
 *   from the image's API payload. When provided and a URL exists for the
 *   requested size, that CDN URL is returned. Caller is responsible for
 *   onError fallback to the proxy URL — the CDN variant 404s until the
 *   proxy `?size=N` route has run at least once.
 * @returns {string|null}
 */
export function withThumbnailSize(url, size, cdnThumbnailUrls = null) {
  if (cdnThumbnailUrls && size && cdnThumbnailUrls[size]) {
    return cdnThumbnailUrls[size];
  }
  if (!url || !size) return url;
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  const resolvedUrl = resolveBackendMediaUrl(url);
  const supportsSize = (
    resolvedUrl.includes('/api/images/') ||
    resolvedUrl.includes('/api/combat-card-decks/')
  );
  if (!supportsSize) return resolvedUrl;
  const sep = resolvedUrl.includes('?') ? '&' : '?';
  return `${resolvedUrl}${sep}size=${size}`;
}
